// src/pages/UserProfile.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Avatar,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Stars as StarsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getApiUrl } from '../config';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHead from '../components/PageHead';

const UserProfile = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellationDate, setCancellationDate] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for subscription success message
    if (location.state?.subscriptionSuccess) {
      setMessage(location.state.message);
      // Clear the location state
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    // Fetch user profile data from backend
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${getApiUrl()}/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setEmail(response.data.email);
        setFirstName(response.data.first_name || '');
        setLastName(response.data.last_name || '');
        setSubscription(response.data.subscription || null);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${getApiUrl()}/profile`, {
        first_name: firstName,
        last_name: lastName,
        password,
        newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setMessage('Profile updated successfully');
      setIsEditing(false);
      setPassword('');
      setNewPassword('');
    } catch (error) {
      setMessage('Error updating profile');
      console.error('Profile update error:', error);
    }
  };

  const handleUpgradeSubscription = () => {
    // Navigate to subscription upgrade page with current plan info
    navigate(`/subscription/upgrade?current=${subscription.plan_type}`);
  };

  const handleCancelSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/cancel-subscription`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setCancellationDate(new Date(response.data.effective_date));
        // Update subscription status locally
        setSubscription(prev => ({
          ...prev,
          status: 'canceling'
        }));
        setMessage('Subscription cancelled successfully. You will have access until the end of your billing period.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setMessage(error.response?.data?.error || 'Failed to cancel subscription');
    }
    setCancelDialogOpen(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <CircularProgress size={60} thickness={4} />
        </motion.div>
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <PageHead 
        title="Profile Settings" 
        description="Manage your AutoBlogPilot account settings and preferences"
      />
      <Box
        sx={{
          background: 'linear-gradient(145deg, #f6f8ff 0%, #f0f4ff 100%)',
          borderRadius: 4,
          p: 4,
          minHeight: '90vh',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4} display="flex" alignItems="center" gap={3}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '2rem',
              }}
            >
              {firstName ? firstName[0].toUpperCase() : <PersonIcon />}
            </Avatar>
            <Box flex={1}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {`${firstName} ${lastName}` || 'Your Profile'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Manage your account settings
              </Typography>
            </Box>
            <Tooltip title={isEditing ? 'Save Changes' : 'Edit Profile'}>
              <IconButton
                onClick={() => isEditing ? document.getElementById('profile-form').requestSubmit() : setIsEditing(true)}
                sx={{
                  bgcolor: isEditing ? 'success.main' : 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: isEditing ? 'success.dark' : 'primary.dark',
                  },
                  width: 48,
                  height: 48,
                }}
              >
                {isEditing ? <SaveIcon /> : <EditIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          {message && (
            <Alert
              severity={message.includes('success') ? 'success' : 'error'}
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
              onClose={() => setMessage('')}
            >
              {message}
            </Alert>
          )}

          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: 'white',
              transition: 'all 0.3s ease',
            }}
          >
            <form id="profile-form" onSubmit={handleSubmit}>
              <Box p={4}>
                <Box mb={4}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Personal Information
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 3,
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    }}
                  >
                    <TextField
                      label="First Name"
                      variant="outlined"
                      fullWidth
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                    <TextField
                      label="Last Name"
                      variant="outlined"
                      fullWidth
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                    <TextField
                      label="Email"
                      variant="outlined"
                      fullWidth
                      value={email}
                      disabled={true}
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                      }}
                    />
                  </Box>
                </Box>

                {isEditing && (
                  <>
                    <Divider sx={{ my: 4 }} />
                    <Box>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Change Password
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 3,
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        }}
                      >
                        <TextField
                          label="Current Password"
                          variant="outlined"
                          type="password"
                          fullWidth
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          InputProps={{
                            startAdornment: <LockIcon sx={{ mr: 1, color: 'action.active' }} />,
                          }}
                        />
                        <TextField
                          label="New Password"
                          variant="outlined"
                          type="password"
                          fullWidth
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          InputProps={{
                            startAdornment: <LockIcon sx={{ mr: 1, color: 'action.active' }} />,
                          }}
                        />
                      </Box>
                    </Box>
                  </>
                )}

                <Divider sx={{ my: 4 }} />
                
                <Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Subscription Details
                  </Typography>
                  {subscription && (
                    <Box sx={{ mt: 2 }}>
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <StarsIcon color="primary" />
                        <Typography variant="h5" color="primary">
                          {subscription.plan_type.toUpperCase()} Plan
                        </Typography>
                        <Chip
                          label={subscription.status.toUpperCase()}
                          color={subscription.status === 'active' ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                      
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="body1" gutterBottom>
                          <strong>Posts Used:</strong> {subscription.monthly_post_limit - subscription.remaining_posts} of {subscription.monthly_post_limit}
                        </Typography>
                        <Box sx={{ mt: 2, mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Box sx={{ flexGrow: 1, bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                              <Box
                                sx={{
                                  width: `${((subscription.monthly_post_limit - subscription.remaining_posts) / subscription.monthly_post_limit) * 100}%`,
                                  bgcolor: 'primary.main',
                                  height: '100%',
                                  borderRadius: 1,
                                  transition: 'width 0.5s ease-in-out',
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {Math.round((subscription.remaining_posts / subscription.monthly_post_limit) * 100)}% remaining
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body1" gutterBottom>
                          <strong>Max Post Length:</strong> {subscription.max_post_length} words
                        </Typography>
                        {subscription.current_period_end && (
                          <Typography variant="body1" gutterBottom>
                            <strong>Renews:</strong> {new Date(subscription.current_period_end).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>

                      {subscription.plan_type !== 'enterprise' && (
                        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleUpgradeSubscription}
                            startIcon={<StarsIcon />}
                          >
                            Upgrade Subscription
                          </Button>
                          
                          {subscription.plan_type !== 'free' && subscription.status !== 'canceling' && (
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => setCancelDialogOpen(true)}
                            >
                              Cancel Subscription
                            </Button>
                          )}
                        </Box>
                      )}

                      {/* Cancel Subscription Dialog */}
                      <Dialog
                        open={cancelDialogOpen}
                        onClose={() => setCancelDialogOpen(false)}
                        maxWidth="sm"
                        fullWidth
                      >
                        <DialogTitle>Cancel Subscription</DialogTitle>
                        <DialogContent>
                          <Typography gutterBottom>
                            Are you sure you want to cancel your subscription?
                          </Typography>
                          <Typography color="text.secondary">
                            You will continue to have access to your current plan until the end of your billing period. After that, your account will be converted to the free plan.
                          </Typography>
                          {subscription?.current_period_end && (
                            <Typography sx={{ mt: 2 }} color="warning.main">
                              Your subscription will remain active until: {new Date(subscription.current_period_end).toLocaleDateString()}
                            </Typography>
                          )}
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setCancelDialogOpen(false)}>
                            Keep Subscription
                          </Button>
                          <Button onClick={handleCancelSubscription} color="error" variant="contained">
                            Yes, Cancel Subscription
                          </Button>
                        </DialogActions>
                      </Dialog>

                    </Box>
                  )}
                </Box>
              </Box>
            </form>
          </Paper>
        </motion.div>
      </Box>
    </Container>
  );
};

export default UserProfile;
